import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Save, Pencil, X, Sparkles, Eye, EyeOff } from "lucide-react";
import ImageComposer from "./ImageComposer";

/**
 * HANDMADE PANEL — Admin
 * - Kart ekle / düzenle / sil / aktif-pasif
 * - Görsel yükle (/api/admin/upload)
 * - Sıralama ok tuşları
 */
export default function HandmadePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null veya item objesi; {id: null} = yeni
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  // Composer state — dosya seçildiğinde open olur, apply edince upload tetiklenir
  const [composerFile, setComposerFile] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/handmade");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => setEditing({ id: null, title: "", description: "", price: "", price_label: "", image_url: "", enabled: true, sort_order: items.length });
  const openEdit = (it) => setEditing({ ...it, price: it.price != null ? String(it.price) : "" });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if (!editing.title.trim()) { toast.error("Başlık zorunlu"); return; }
    if (!editing.image_url.trim()) { toast.error("Görsel zorunlu"); return; }
    const payload = {
      title: editing.title.trim(),
      description: (editing.description || "").trim(),
      price: editing.price ? parseFloat(editing.price) : null,
      price_label: (editing.price_label || "").trim() || null,
      image_url: editing.image_url.trim(),
      enabled: editing.enabled !== false,
      sort_order: parseInt(editing.sort_order) || 0,
    };
    try {
      if (editing.id) {
        await api.patch(`/admin/handmade/${editing.id}`, payload);
        toast.success("✓ Güncellendi");
      } else {
        await api.post("/admin/handmade", payload);
        toast.success("✓ Eklendi");
      }
      close();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const toggleEnabled = async (it) => {
    try {
      await api.patch(`/admin/handmade/${it.id}`, { enabled: !(it.enabled !== false) });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Değiştirilemedi");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`"${it.title}" kartı silinsin mi?`)) return;
    try {
      await api.delete(`/admin/handmade/${it.id}`);
      toast.success("Silindi");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    try {
      await api.post("/admin/handmade/reorder", { ids: next.map((x) => x.id) });
    } catch {
      await refresh();
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    // Composer'ı aç — kullanıcı kırp/arka plan/logo ayarlar, apply edince uploadBlob çağırır
    setComposerFile(file);
  };

  const uploadBlob = async (blob) => {
    if (!editing) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "composed.jpg", { type: "image/jpeg" }));
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((s) => ({ ...s, image_url: r.data?.url || "" }));
      toast.success("✓ Görsel düzenlendi ve yüklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yüklenemedi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#D9653B]/5 border border-[#D9653B]/30 rounded-2xl p-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-lg font-bold text-[#1B2A22] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D9653B]" />
            Günlük El Yapımı Ürünler ({items.length})
          </h3>
          <p className="text-xs text-[#1B2A22]/70 mt-1">
            Ana sayfada hero'nun altında geniş kartlarla gösterilir. Müşteri WhatsApp'tan sipariş eder — sepete eklenmez.
            <br />Her kartta: görsel + başlık + fiyat (opsiyonel) + açıklama.
          </p>
        </div>
        <Button
          onClick={openNew}
          data-testid="handmade-new-btn"
          className="rounded-full bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold"
        >
          <Plus className="w-4 h-4 mr-1" /> Yeni Kart Ekle
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-[#1B2A22]/50">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8E2D5] rounded-2xl p-10 text-center">
          <Sparkles className="w-10 h-10 text-[#1B2A22]/20 mx-auto mb-2" />
          <p className="text-sm text-[#1B2A22]/60">Henüz el yapımı kart yok. <b>Yeni Kart Ekle</b>'ye bas.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((it, i) => (
            <div
              key={it.id}
              data-testid={`handmade-admin-card-${i}`}
              className={`bg-white border rounded-2xl overflow-hidden ${it.enabled === false ? "opacity-50 border-[#1B2A22]/20" : "border-[#E8E2D5]"}`}
            >
              <div className="aspect-[4/3] bg-[#1B2A22]/5 relative">
                {it.image_url ? (
                  <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" />
                ) : null}
                <div className="absolute top-2 right-2 flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleEnabled(it)}
                    className="h-7 w-7 p-0 rounded-full bg-white/90"
                    title={it.enabled === false ? "Göster" : "Gizle"}
                    data-testid={`handmade-toggle-${i}`}
                  >
                    {it.enabled === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5 text-[#2E5A36]" />}
                  </Button>
                </div>
                <div className="absolute top-2 left-2 flex flex-col gap-0.5">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="w-6 h-6 rounded-full bg-white/90 text-[#1B2A22]/70 disabled:opacity-20">▲</button>
                  <button type="button" onClick={() => move(i, +1)} disabled={i === items.length - 1} className="w-6 h-6 rounded-full bg-white/90 text-[#1B2A22]/70 disabled:opacity-20">▼</button>
                </div>
              </div>
              <div className="p-3">
                <h4 className="font-heading font-bold text-sm text-[#1B2A22] truncate">{it.title}</h4>
                {(it.price_label || (typeof it.price === "number" && it.price > 0)) && (
                  <p className="text-xs text-[#D9653B] font-bold font-mono mt-0.5">
                    {it.price_label || `₺${it.price.toFixed(2)}`}
                  </p>
                )}
                {it.description && <p className="text-[11px] text-[#1B2A22]/60 mt-1 line-clamp-2">{it.description}</p>}
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(it)} className="rounded-full flex-1 h-8" data-testid={`handmade-edit-${i}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Düzenle
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(it)} className="rounded-full h-8 text-red-600 border-red-300" data-testid={`handmade-delete-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal (inline, basit) */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="handmade-editor">
          <div className="bg-[#FAF7F2] rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#FAF7F2] border-b border-[#E8E2D5] px-5 py-3 flex items-center justify-between">
              <h3 className="font-heading font-bold">{editing.id ? "Kartı Düzenle" : "Yeni El Yapımı Kart"}</h3>
              <button type="button" onClick={close} className="text-[#1B2A22]/60 hover:text-[#1B2A22]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label className="text-xs">Görsel *</Label>
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-24 h-24 rounded-xl bg-white border border-[#E8E2D5] overflow-hidden flex items-center justify-center">
                    {editing.image_url ? <img src={editing.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-[#1B2A22]/40">önizleme</span>}
                  </div>
                  <div className="flex-1">
                    <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full bg-[#2E5A36] hover:bg-[#24472B] text-white font-heading font-bold">
                      <Upload className="w-4 h-4 mr-2" /> {uploading ? "Yükleniyor..." : "Görsel Yükle"}
                    </Button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="handmade-file-input" />
                    <Input className="mt-2 bg-white text-[11px] font-mono" value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="/api/files/..." data-testid="handmade-image-url" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs">Başlık *</Label>
                <Input className="bg-white" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="örn: Ev yapımı hellim 400 gr" data-testid="handmade-title" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Fiyat (₺) — opsiyonel</Label>
                  <Input type="number" step="0.01" className="bg-white font-mono" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} placeholder="120" data-testid="handmade-price" />
                </div>
                <div>
                  <Label className="text-xs">Serbest etiket (fiyat yerine)</Label>
                  <Input className="bg-white" value={editing.price_label || ""} onChange={(e) => setEditing({ ...editing, price_label: e.target.value })} placeholder="örn: ₺120 / tabak" data-testid="handmade-price-label" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Açıklama</Label>
                <Textarea className="bg-white min-h-[80px]" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Günlük taze, el yapımı..." data-testid="handmade-desc" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={editing.enabled !== false} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} className="w-4 h-4 accent-[#2E5A36]" data-testid="handmade-enabled" />
                <span className={editing.enabled !== false ? "text-[#2E5A36] font-bold" : "text-[#1B2A22]/50"}>
                  {editing.enabled !== false ? "✓ Aktif (ana sayfada görünür)" : "⏸ Pasif (gizli)"}
                </span>
              </label>
            </div>
            <div className="sticky bottom-0 bg-[#FAF7F2] border-t border-[#E8E2D5] px-5 py-3 flex justify-end gap-2">
              <Button variant="outline" onClick={close} className="rounded-full">İptal</Button>
              <Button onClick={save} className="rounded-full bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold" data-testid="handmade-save">
                <Save className="w-4 h-4 mr-2" /> Kaydet
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Composer — HandmadePanel dosya seçince ürün görseli stüdyosu açılır */}
      <ImageComposer
        open={!!composerFile}
        file={composerFile}
        onCancel={() => {
          setComposerFile(null);
          if (fileRef.current) fileRef.current.value = "";
        }}
        onReady={async (blob) => {
          setComposerFile(null);
          await uploadBlob(blob);
        }}
      />
    </div>
  );
}
